import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { GroupService, InvitationRead, InvitationService } from '../../../../../js/api';
import { groupQueryKeys } from '../../../../../js/app/query-keys-factories/groups';
import { invitationQueryKeys } from '../../../../../js/app/query-keys-factories/invitations';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { setFormErrorsFromApiErrors } from '../../../../../js/utils/forms';
import { BrandSwitch } from '../../../components/BrandSwitch';
import { Button } from '../../../components/button';
import { Dialog, DialogBody, DialogTitle, DialogActions } from '../../../components/dialog';
import { Field, Label } from '../../../components/fieldset';
import { Input } from '../../../components/input';

const invitationValidation = z.object({
  email: z.string().email(),
  group_names: z.union([z.string().min(1), z.array(z.string()), z.boolean()]),
});

type InviteFormData = z.infer<typeof invitationValidation>;

interface InviteUserDialogProps {
  open: boolean;
  onClose: () => void;
}

export function InviteUserDialog({ open, onClose }: InviteUserDialogProps) {
  const form = useForm<InviteFormData>({
    resolver: zodResolver(invitationValidation),
    defaultValues: { email: '', group_names: [] as string[] },
  });

  const permissionGroups = useQuery({
    queryKey: groupQueryKeys.list({ limit: 500 }),
    queryFn: async () => {
      const data = await GroupService.groupList({ limit: 500 });
      return data.results || [];
    },
    keepPreviousData: true,
  });

  useEffect(() => {
    const DEFAULT_GROUPS = ['connect_with_providers', 'schedule_shifts'];
    const apiGroupNames = permissionGroups.data?.map((group) => group.code);
    const preSelectedGroupNames = DEFAULT_GROUPS.filter((groupName) =>
      apiGroupNames?.includes(groupName)
    );
    if (preSelectedGroupNames.length > 0) {
      form.setValue('group_names', preSelectedGroupNames);
    }
  }, [permissionGroups.data]);

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: InvitationRead) => {
      await InvitationService.invitationCreate({ requestBody: data });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(invitationQueryKeys.all());
    },
  });

  const onSubmit = async (data: InviteFormData) => {
    let submitData: InvitationRead;
    if (typeof data.group_names === 'string') {
      submitData = { ...data, group_names: [data.group_names] };
    } else if (typeof data.group_names === 'boolean') {
      submitData = { ...data, group_names: [] };
    } else {
      submitData = { ...data, group_names: data.group_names || [] };
    }

    try {
      await mutation.mutateAsync(submitData);
      toast.success('User invited successfully.');
      form.setValue('email', '');
      onClose();
    } catch (errors) {
      setFormErrorsFromApiErrors(
        errors as Parameters<typeof setFormErrorsFromApiErrors>[0],
        form.setError
      );
      handleApiError(errors);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="4xl">
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DialogTitle>Invite User</DialogTitle>
        <DialogBody>
          <div className="tw:space-y-6">
            <Field>
              <Label htmlFor="invite-email">
                Email
                <span className="tw:text-stoplight-danger">*</span>
              </Label>
              <Input
                id="invite-email"
                type="email"
                {...form.register('email')}
                className="tw:w-full"
                data-invalid={!!form.formState.errors.email}
              />
              {form.formState.errors.email && (
                <p className="tw:mt-1 tw:text-sm tw:text-stoplight-danger">
                  {form.formState.errors.email.message}
                </p>
              )}
            </Field>

            <div>
              <p className="tw:mb-3 tw:mt-3 tw:font-semibold tw:text-zinc-950 tw:dark:text-white">
                Set the appropriate User Permissions
              </p>

              {permissionGroups.isLoading && (
                <div className="tw:flex tw:items-center tw:justify-center tw:py-8">
                  <div
                    className="tw:h-8 tw:w-8 tw:animate-spin tw:rounded-full tw:border-2 tw:border-brand-green-0 tw:border-t-transparent"
                    aria-label="loading indicator"
                  />
                </div>
              )}

              {permissionGroups.isError && (
                <div className="tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:px-4 tw:py-3 tw:text-red-700 tw:dark:border-red-800 tw:dark:bg-red-950/30 tw:dark:text-red-400">
                  <p className="tw:font-semibold">Something went wrong</p>
                  <p className="tw:text-sm">We couldn&apos;t retrieve the list of permissions.</p>
                </div>
              )}

              {permissionGroups.isSuccess && permissionGroups.data && (
                <Controller
                  name="group_names"
                  control={form.control}
                  render={({ field }) => (
                    <div className="tw:space-y-4">
                      {permissionGroups.data.map((group) => {
                        const selected = Array.isArray(field.value)
                          ? field.value.includes(group.code)
                          : false;
                        return (
                          <div key={group.code} className="tw:flex tw:gap-4 tw:items-start">
                            <BrandSwitch
                              className="tw:!mt-1"
                              checked={selected}
                              onChange={(checked) => {
                                const current = Array.isArray(field.value) ? field.value : [];
                                if (checked) {
                                  field.onChange([...current, group.code]);
                                } else {
                                  field.onChange(current.filter((c) => c !== group.code));
                                }
                              }}
                            />
                            <div className="tw:flex tw:flex-col tw:gap-0.5 tw:min-w-0">
                              <span className="tw:font-medium">{group.title}</span>
                              {group.description && (
                                <span className="tw:text-xs tw:text-zinc-500">
                                  {group.description}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                />
              )}
            </div>
          </div>
        </DialogBody>
        <DialogActions>
          <Button type="button" plain onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="brandgreen1"
            disabled={mutation.isPending || permissionGroups.isLoading}
          >
            {mutation.isPending ? 'Inviting...' : 'Invite'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
